import React, { useState, useCallback, useMemo } from "react";
import CalendarDayView from "@amzn/meridian/calendar-day-view";
import Tile from "@amzn/meridian/tile";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { convertUnix } from "../../utilities/convert-unix";
import { Heading } from "@aws-amplify/ui-react";
import { utcToZonedTime } from "date-fns-tz";
import { getDisplayQuantity } from "../../utilities/parent-child-conversion";
import { convertParentChild } from "../Constants";

const getArrivalTime = (truck, time_zone) => {
  return convertUnix(
      truck.firstAppointmentTime ||
      truck.expectedArrivalTime[0] ||
      (truck.scheduledArrivals && truck.scheduledArrivals[0]),
    time_zone
  );
};

function convertDateFormat(dateString) {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
}

const getCurrentTime = (now, time_zone) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: time_zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(now);
  const values = parts.reduce((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}`;
};

const TruckEventTile = React.memo(({ distributorData, isPast }) => {
  return (
    <Tile accent={isPast ? "error" : "success"}>
      <Text type="h300">{distributorData.distributor}</Text>
      <Text type="p100">
        {`Total Units${convertParentChild === 1 ? ' Child' : ''}: ${distributorData.totalUnits.toLocaleString()}`}
      </Text>
      {Object.entries(distributorData.tempZoneUnits).map(([zone, units]) => (
        <Text key={zone} type="p100">
          {zone}: {units.toLocaleString()}
        </Text>
      ))}
    </Tile>
  );
});

const Hour = React.memo(
  ({ time, trucks, date, time_zone }) => {
    const isBeforeNow = useMemo(() => {
      const currentDate = new Date();
      const slotDateTime = new Date(date);
      const [hours, minutes] = time.split(":").map(Number);
      slotDateTime.setHours(hours, minutes, 0, 0);
      return slotDateTime < currentDate;
    }, [date, time]);

    const slotData = useMemo(() => {
      const hour = parseInt(time.split(":")[0], 10);
      const shipmentsInSlot = trucks.filter((shipment) => {
        const shipmentTime = new Date(getArrivalTime(shipment, time_zone));
        return (
          shipmentTime.getHours() === hour &&
          shipmentTime.toDateString() === date.toDateString()
        );
      });

      const validShipmentsInSlot = shipmentsInSlot.filter((s) => {
        if (
          s.loadId === "PO Pending Schedule" ||
          s.distributorName === "PO Pending Schedule"
        ) {
          return false;
        }
        if (s.loadType === "Transship") {
          return s.loadId && !s.loadId.includes("PO Pending");
        } else {
          if (s.loadId === "Not Yet Manifested") {
            return s.shipmentId && s.shipmentId.length > 0;
          }
          return s.distributorName && s.distributorName !== "Unknown";
        }
      });

      if (validShipmentsInSlot.length === 0) return null;

      // Group shipments by distributor
      const distributorGroups = validShipmentsInSlot.reduce((acc, shipment) => {
        let distributorName;
        if (
          shipment.loadType !== "Transship" &&
          shipment.distributorName.startsWith("WFM")
        ) {
          distributorName = "WFM";
        } else if (
          shipment.loadType !== "Transship" &&
          shipment.distributorName.includes("Spartan Nash")
        ) {
          distributorName = "Spartan Nash";
        } else if (
          shipment.loadType !== "Transship" &&
          shipment.distributorName.includes("KeHe")
        ) {
          distributorName = "KeHe";
        } else if (
          shipment.loadType !== "Transship" &&
          shipment.distributorName.includes("UNFI")
        ) {
          distributorName = "UNFI";
        } else if (
          shipment.loadType === "Transship" &&
          shipment.shipmentId &&
          shipment.shipmentId.length > 0
        ) {
          distributorName = `${shipment.source} - ${shipment.shipmentId[0]}`;
        } else {
          distributorName = shipment.distributorName;
        }

        if (!acc[distributorName]) {
          acc[distributorName] = {
            distributor: distributorName,
            shipments: [],
            tempZoneUnits: {},
            totalUnits: 0,
          };
        }

        acc[distributorName].shipments.push(shipment);

        if (shipment.tempZone) {
          const displayUnits = getDisplayQuantity(shipment.expectedUnits, shipment.childQuantity) || 0;
          acc[distributorName].tempZoneUnits[shipment.tempZone] =
            (acc[distributorName].tempZoneUnits[shipment.tempZone] || 0) + displayUnits;
          acc[distributorName].totalUnits += displayUnits;
        }

        return acc;
      }, {});

      return Object.values(distributorGroups);
    }, [trucks, time, date, time_zone]);

    return slotData ? (
      <Column spacing="300">
        {slotData.map((distributorData, index) => (
          <TruckEventTile
            key={`${distributorData.distributor}-${index}`}
            distributorData={distributorData}
            isPast={isBeforeNow}
          />
        ))}
      </Column>
    ) : null;
  },
  (prevProps, nextProps) =>
    prevProps.time === nextProps.time &&
    prevProps.trucks === nextProps.trucks &&
    prevProps.date.toDateString() === nextProps.date.toDateString() &&
    prevProps.time_zone === nextProps.time_zone
);

export const TruckCalendarView = ({ trucks, time_zone }) => {
  const [date, setDate] = useState(() => {
    // Get the current date in the specified time zone
    const nowInTimeZone = utcToZonedTime(new Date(), time_zone);
    return nowInTimeZone;
  });

  const onClickToday = useCallback(() => {
    const nowInTimeZone = utcToZonedTime(new Date(), time_zone);
    setDate(nowInTimeZone);
  }, [time_zone]);

  const formattedDate = useMemo(() => {
    const dateInTimeZone = utcToZonedTime(date, time_zone);
    return dateInTimeZone.toISOString().split("T")[0];
  }, [date, time_zone]);

  const currentTimeInZone = useMemo(() => {
    const now = new Date();
    return getCurrentTime(now, time_zone);
  }, [time_zone]);

  // Function to check if the selected date is tomorrow or later
  const isTomorrowOrLater = useMemo(() => {
    const todayInTimeZone = utcToZonedTime(new Date(), time_zone);
    todayInTimeZone.setHours(0, 0, 0, 0);
    const tomorrowInTimeZone = new Date(todayInTimeZone);
    tomorrowInTimeZone.setDate(tomorrowInTimeZone.getDate() + 1);
    return date >= tomorrowInTimeZone;
  }, [date, time_zone]);

  return (
    <Column
      type="outline"
      heights={["auto", "fill"]}
      spacing="none"
      height="auto"
      width="auto"
    >
      <Row
        spacingInset="400"
        alignmentHorizontal="justify"
        widths={[75, "fit", 75]}
      >
        <Button type="secondary" onClick={onClickToday}>
          Today
        </Button>
        <Heading level={2}>{convertDateFormat(date)}</Heading>
        <Button
          type="secondary"
          onClick={() => {
            const nextDay = new Date(date);
            nextDay.setDate(nextDay.getDate() + 1);
            setDate(nextDay);
          }}
          disabled={isTomorrowOrLater}
        >
          Next Day
        </Button>
      </Row>
      <CalendarDayView
        viewDate={formattedDate}
        now={currentTimeInZone}
        timeRangeStart="00:00"
        timeRangeEnd="23:59"
        renderHour={(props) => (
          <Hour {...props} trucks={trucks} date={date} time_zone={time_zone} />
        )}
      />
    </Column>
  );
};
